import React, { useCallback, useEffect, useRef, useState } from "react";

import "./styles.css";

const SWIPE_THRESHOLD = 56;
const EXIT_DURATION = 220;

const ICONS = {
  success: (
    <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
      <path
        d="M4.5 10.6l3.6 3.6L15.5 6.8"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  error: (
    <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
      <path
        d="M6.4 6.4l7.2 7.2M13.6 6.4l-7.2 7.2"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
      />
    </svg>
  ),
  warning: (
    <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
      <path
        d="M10 5.4v5.2"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
      />
      <circle cx="10" cy="14.4" r="1.25" fill="currentColor" />
    </svg>
  ),
  info: (
    <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
      <circle cx="10" cy="5.6" r="1.25" fill="currentColor" />
      <path
        d="M10 9.2v5.4"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
      />
    </svg>
  ),
};

const CustomToast = ({ id, type, title, message, duration, action, onClose }) => {
  const [leaving, setLeaving] = useState(false);
  const [paused, setPaused] = useState(false);
  const [drag, setDrag] = useState(null);

  const timerRef = useRef(null);
  const remainingRef = useRef(duration);
  const startedAtRef = useRef(0);
  const originRef = useRef(null);
  const closingRef = useRef(false);

  const dismiss = useCallback(() => {
    if (closingRef.current) return;
    closingRef.current = true;

    if (timerRef.current) clearTimeout(timerRef.current);

    setLeaving(true);
    setTimeout(() => onClose(id), EXIT_DURATION);
  }, [id, onClose]);

  const resumeTimer = useCallback(() => {
    if (duration <= 0 || closingRef.current) return;

    startedAtRef.current = Date.now();
    timerRef.current = setTimeout(dismiss, remainingRef.current);
  }, [duration, dismiss]);

  const pauseTimer = useCallback(() => {
    if (duration <= 0 || closingRef.current) return;

    if (timerRef.current) clearTimeout(timerRef.current);
    remainingRef.current = Math.max(
      0,
      remainingRef.current - (Date.now() - startedAtRef.current)
    );
  }, [duration]);

  useEffect(() => {
    resumeTimer();

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [resumeTimer]);

  const hold = () => {
    setPaused(true);
    pauseTimer();
  };

  const release = () => {
    setPaused(false);
    resumeTimer();
  };

  const runAction = (event) => {
    event.stopPropagation();
    action.onClick?.();
    dismiss();
  };

  const onPointerDown = (event) => {
    if (event.target.closest("button")) return;

    originRef.current = { x: event.clientX, y: event.clientY };
    event.currentTarget.setPointerCapture(event.pointerId);
    hold();
  };

  const onPointerMove = (event) => {
    if (!originRef.current) return;

    const dx = event.clientX - originRef.current.x;
    const dy = event.clientY - originRef.current.y;

    setDrag({ x: dx > 0 ? dx : dx * 0.25, y: dy < 0 ? dy : dy * 0.25 });
  };

  const onPointerUp = () => {
    if (!originRef.current) return;

    originRef.current = null;

    if (drag && (drag.x > SWIPE_THRESHOLD || drag.y < -SWIPE_THRESHOLD)) {
      dismiss();
      return;
    }

    setDrag(null);
    release();
  };

  const dragging = originRef.current != null;
  const travel = drag ? Math.max(Math.abs(drag.x), Math.abs(drag.y)) : 0;

  const classes = [
    "hex-toast",
    `hex-toast--${type}`,
    leaving && "hex-toast--leaving",
    dragging && "hex-toast--dragging",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className={classes}
      style={
        drag
          ? {
              transform: `translate3d(${drag.x}px, ${drag.y}px, 0)`,
              opacity: Math.max(0.35, 1 - travel / 180),
            }
          : undefined
      }
      role={type === "error" ? "alert" : "status"}
      aria-live={type === "error" ? "assertive" : "polite"}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onMouseEnter={hold}
      onMouseLeave={release}
    >
      <span className="hex-toast-icon" aria-hidden="true">
        {ICONS[type] || ICONS.info}
      </span>

      <span className="hex-toast-text">
        {title ? <span className="hex-toast-title">{title}</span> : null}
        <span className="hex-toast-message">{message}</span>
      </span>

      {action?.label ? (
        <button type="button" className="hex-toast-action" onClick={runAction}>
          {action.label}
        </button>
      ) : null}

      <button
        type="button"
        className="hex-toast-close"
        onClick={dismiss}
        aria-label="Dismiss notification"
      >
        <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true">
          <path
            d="M4.2 4.2l7.6 7.6M11.8 4.2l-7.6 7.6"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      </button>

      {duration > 0 ? (
        <span className="hex-toast-progress" aria-hidden="true">
          <span
            className="hex-toast-progress-fill"
            style={{
              animationDuration: `${duration}ms`,
              animationPlayState: paused || leaving ? "paused" : "running",
            }}
          />
        </span>
      ) : null}
    </div>
  );
};

export default CustomToast;
